import {
  CardCarousel,
  CtaLink,
  Grid,
  HeaderTextBlock,
  LayoutContainer,
  PageSection,
  PageSectionContent,
  ctaVariants,
} from '@greystar/designsystem';
import {
  getDictionaryEntry,
  getLinkIcon,
  getRoundedTop,
  getTopColour,
  getWindowLocation,
  useAnalytics,
  useFormattedAddress,
} from '@greystar/lib';
import { EventProperty } from '@greystar/lib/src/types/property-types';
import { CardListProps, CardProps } from '@greystar/ui/src/component-props';
import {
  Link as JssLink,
  Text as JssText,
  Placeholder,
  useSitecoreContext,
  withDatasourceCheck,
} from '@sitecore-jss/sitecore-jss-nextjs';
import React from 'react';
import Card from './card';
import { useI18n } from 'next-localization';

export const CardList = (props: CardListProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();

  if (!props.fields) {
    return <></>;
  }

  type CtaVariant = (typeof ctaVariants)[keyof typeof ctaVariants];

  const { title, heading, ctaLink } = props.fields;
  const topColour = getTopColour(props.params?.Styles);
  const isRoundedTop = getRoundedTop(props.params?.Styles);

  const classNames = props.params?.className;
  const linkField = <JssLink field={ctaLink} />;
  const headingStyles = props?.params?.headingStyles as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';

  return (
    <PageSection
      data-top-colour={topColour}
      topColour={topColour}
      isRoundedTop={isRoundedTop}
      className={classNames}
    >
      <PageSectionContent>
        <HeaderTextBlock direction="reverse">
          <HeaderTextBlock.Subtitle variant="secondary" subtitle={<JssText field={title} />} />
          <HeaderTextBlock.Header
            as={headingStyles}
            header={<JssText field={heading} as={props.params?.headingStyles ?? undefined} />}
          />
        </HeaderTextBlock>

        {!sitecoreContext.pageEditing &&
        props.rendering?.params?.isCarousel &&
        props.rendering.params.isCarousel === '1' ? (
          <Placeholder
            name={`card-list-card-${props.params.DynamicPlaceholderId}`}
            rendering={props.rendering}
            render={(components) => (
              <CardCarouselWrapper components={components} heading={heading?.value} />
            )}
          />
        ) : (
          <>
            <LayoutContainer
              margin="auto"
              paddingTop="2xl"
              paddingLeft="responsiveXlg"
              paddingRight="responsiveXlg"
              maxWidth="2xlg"
            >
              {sitecoreContext.pageEditing ? (
                <Placeholder
                  name={`card-list-card-${props.params.DynamicPlaceholderId}`}
                  rendering={props.rendering}
                  isCarousel="false"
                />
              ) : (
                <Placeholder
                  name={`card-list-card-${props.params.DynamicPlaceholderId}`}
                  rendering={props.rendering}
                  render={(components) => (
                    <CardListWrapper components={components} heading={heading?.value} />
                  )}
                />
              )}
            </LayoutContainer>
            {ctaLink?.value?.href && (
              <LayoutContainer justifyContent="end" paddingTop="md">
                <CtaLink
                  isEditable={sitecoreContext.pageEditing}
                  label={ctaLink.value.text}
                  href={ctaLink.value.href}
                  variant={props.params.ctaVariant as CtaVariant}
                  icon={getLinkIcon({
                    iconName: props.params.iconList,
                    iconPosition: props.params.iconPosition,
                    variant: props.params.ctaVariant,
                  })}
                >
                  {linkField}
                </CtaLink>
              </LayoutContainer>
            )}
          </>
        )}
      </PageSectionContent>
    </PageSection>
  );
};

function CardCarouselWrapper({
  components,
  heading,
}: Readonly<{
  components: React.ReactNode[];
  heading?: string;
}>) {
  const analytics = useAnalytics();
  const { sitecoreContext } = useSitecoreContext();
  const { t } = useI18n();
  const property: EventProperty = sitecoreContext?.property ?? {};
  const location = property?.location;
  const property_location = useFormattedAddress(location);

  const pushDataLayerEvent = (actionType: 'left' | 'right') => {
    const dataEvent = {
      event: 'carousel',
      action_type: actionType,
      state: location?.state ?? '',
      property_location: property_location,
      property_id: property.id ?? '',
      property_name: property.name ?? '',
      module_name: heading ?? 'Card Carousel',
      inpage_location: 'middle',
      click_text: actionType === 'left' ? 'Previous' : 'Next',
      click_type: 'internal',
      destination_url: getWindowLocation(),
    };

    analytics.trackEvent('carousel', dataEvent);
  };

  const pushPaginationAnalytics = (index: number) => {
    const dataEvent = {
      event: 'carousel',
      action_type: 'navigation indicator',
      state: location?.state ?? '',
      property_location: property_location,
      property_name: property.name ?? '',
      module_name: heading ?? 'Card Carousel',
      inpage_location: 'middle',
      click_text: `Pagination Dot ${index + 1}`,
      click_type: 'internal',
      destination_url: getWindowLocation(),
    };

    analytics.trackEvent('carousel', dataEvent);
  };

  const filteredCards: React.ReactElement<CardProps>[] = components.filter(
    (card): card is React.ReactElement<CardProps> =>
      React.isValidElement(card) && card.props?.fields !== undefined
  );

  return (
    <LayoutContainer paddingTop="2xl" display={null}>
      <CardCarousel
        onPipClick={pushPaginationAnalytics}
        onNavigateLeft={() => pushDataLayerEvent('left')}
        onNavigateRight={() => pushDataLayerEvent('right')}
        prevLabel={getDictionaryEntry(t('carousel-card-previous'), 'Carousel Card Previous')}
        nextLabel={getDictionaryEntry(t('carousel-card-next'), 'Carousel Card Next')}
      >
        {filteredCards.map((card) => renderCard(card, heading))}
      </CardCarousel>
    </LayoutContainer>
  );
}

function renderCard(card: React.ReactElement<CardProps>, heading?: string) {
  const props = card.props;
  return <Card key={props.rendering?.uid} {...props} isCarousel sectionHeading={heading} />;
}

function CardListWrapper({
  components,
  heading,
}: Readonly<{
  components: React.ReactNode[];
  heading?: string;
}>) {
  const validComponents: React.ReactElement[] = components.filter(React.isValidElement);

  return (
    <Grid>
      {validComponents.map((component, index) => (
        <Grid.Item
          key={component.props.rendering?.uid}
          smColumnSpan={4}
          mdColumnSpan={3}
          mdColumnEnd={index % 2 === 0 ? 5 : 8}
          lgColumnSpan={4}
        >
          {renderCard(component, heading)}
        </Grid.Item>
      ))}
    </Grid>
  );
}

export default withDatasourceCheck()<CardListProps>(CardList);
